import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Eye, Search } from 'lucide-react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import DOMPurify from 'dompurify';

interface SentEmail {
  id: string;
  recipient_email: string;
  subject: string;
  body_html: string;
  body_text: string;
  sent_at: string;
  status: string;
  client_id: string;
  clients?: {
    full_name: string;
  };
  email_templates?: {
    template_name: string;
  };
}

export default function SentEmailTable() {
  const [search, setSearch] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<SentEmail | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const { data: sentEmails, isLoading } = useQuery({
    queryKey: ['sent-emails', search],
    queryFn: async () => {
      let query = supabase
        .from('sent_emails')
        .select(`
          *,
          clients(full_name),
          email_templates(template_name)
        `)
        .order('sent_at', { ascending: false });

      if (search) {
        query = query.or(`recipient_email.ilike.%${search}%,subject.ilike.%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SentEmail[];
    },
  });

  const handleViewEmail = (email: SentEmail) => {
    setSelectedEmail(email);
    setViewModalOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      sent: 'default',
      pending: 'secondary',
      failed: 'destructive',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or subject..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading sent emails...
          </div>
        ) : sentEmails && sentEmails.length > 0 ? (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sentEmails.map((email) => (
                  <TableRow key={email.id}>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(email.sent_at), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell>{email.clients?.full_name || 'N/A'}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {email.recipient_email}
                    </TableCell>
                    <TableCell className="text-sm">
                      {email.email_templates?.template_name || 'Manual'}
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate">
                      {email.subject}
                    </TableCell>
                    <TableCell>{getStatusBadge(email.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewEmail(email)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No sent emails found
          </div>
        )}
      </div>

      {selectedEmail && (
        <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Email Details</DialogTitle>
              <DialogDescription>
                Sent on {format(new Date(selectedEmail.sent_at), 'MMMM d, yyyy at h:mm a')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium mb-1">To:</p>
                  <p className="text-sm text-muted-foreground">{selectedEmail.recipient_email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">Client:</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedEmail.clients?.full_name || 'N/A'}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-1">Subject:</p>
                <p className="text-sm text-muted-foreground">{selectedEmail.subject}</p>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Email Content:</p>
                <div className="rounded-lg border p-6 bg-background">
                  <div
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(selectedEmail.body_html, {
                        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'img', 'div', 'span', 'table', 'thead', 'tbody', 'tr', 'td', 'th'],
                        ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'style']
                      })
                    }}
                    className="prose prose-sm max-w-none"
                  />
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
